<template>
  <div>
    <!-- Menu Layout -->
    <v-menu v-if="layout === 'menu'" offset-y>
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <primary-button :disabled="!qrCode">
            <v-icon left>mdi-download</v-icon>
            {{ $t("download_for_print") }}
          </primary-button>
        </div>
      </template>
      <v-list>
        <v-list-item @click="downloadSVG" :disabled="!qrCode">
          <v-list-item-icon>
            <v-icon>mdi-vector-square</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>SVG</v-list-item-title>
            <v-list-item-subtitle>Vektorski format</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-list-item @click="downloadPNG" :disabled="!qrCode">
          <v-list-item-icon>
            <v-icon>mdi-file-image</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>PNG (600 DPI)</v-list-item-title>
            <v-list-item-subtitle>Visoka rezolucija</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-list-item @click="downloadPDF" :disabled="!qrCode">
          <v-list-item-icon>
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>PDF</v-list-item-title>
            <v-list-item-subtitle>Spreman za štampu</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-list-item @click="downloadEPS" :disabled="!qrCode">
          <v-list-item-icon>
            <v-icon>mdi-file-document</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>EPS</v-list-item-title>
            <v-list-item-subtitle>Profesionalni dizajn</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Default Layout -->
    <div v-else>
      <!-- Download Section -->
      <v-card-subtitle class="text-center pb-2">
        {{ $t("download_for_print") || "Download za štampu" }}
      </v-card-subtitle>

      <!-- Primary formats -->
      <v-row justify="center" class="mb-2">
        <v-col cols="12" sm="6" class="px-2">
          <v-btn @click="downloadSVG" color="primary" outlined :disabled="!qrCode" block>
            <v-icon left>mdi-vector-square</v-icon>
            SVG
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" class="px-2">
          <v-btn @click="downloadPNG" color="success" outlined :disabled="!qrCode" block>
            <v-icon left>mdi-file-image</v-icon>
            PNG (600 DPI)
          </v-btn>
        </v-col>
      </v-row>

      <!-- Professional formats -->
      <v-row justify="center" class="mb-2">
        <v-col cols="12" sm="6" class="px-2">
          <v-btn @click="downloadPDF" color="red" outlined :disabled="!qrCode" block>
            <v-icon left>mdi-file-pdf-box</v-icon>
            PDF
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" class="px-2">
          <v-btn @click="downloadEPS" color="purple" outlined :disabled="!qrCode" block>
            <v-icon left>mdi-file-document</v-icon>
            EPS
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import QRCodeSVG from "qrcode-svg";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "QrCodeExport",
  mixins: [hasSnackbar],

  props: {
    qrCode: {
      type: String,
      required: true,
      default: "",
    },
    fileName: {
      type: String,
      default: "qr_code",
    },
    title: {
      type: String,
      default: "QR Code",
    },
    layout: {
      type: String,
      default: "default",
      validator: (value) => ["default", "menu"].includes(value),
    },
  },

  computed: {
    sanitizedFileName() {
      return (
        this.fileName
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "") || "qr_code"
      );
    },
  },

  methods: {
    generateSVG() {
      if (!this.qrCode) return "";

      const qr = new QRCodeSVG(this.qrCode, {
        width: 400,
        height: 400,
        color: "#000000",
        background: "#ffffff",
        padding: 4, // quiet zone
        join: true, // smoother edges
        container: "svg-viewbox",
      });

      return qr.svg();
    },

    generateHighResPNG() {
      return new Promise((resolve, reject) => {
        if (!this.qrCode) {
          reject(new Error("Nema QR koda"));
          return;
        }

        // Create high-res canvas (600 DPI equivalent)
        const canvas = document.createElement("canvas");
        const size = 1200; // 600 DPI for 2 inch print

        canvas.width = size;
        canvas.height = size;

        // Import QRCode for canvas generation
        import("qrcode")
          .then((QRCode) => {
            QRCode.toCanvas(
              canvas,
              this.qrCode,
              {
                width: size,
                margin: 4, // quiet zone
                color: {
                  dark: "#000000",
                  light: "#FFFFFF00", // transparent background
                },
                errorCorrectionLevel: "M",
              },
              (error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve(canvas);
                }
              }
            );
          })
          .catch(reject);
      });
    },

    async downloadPNG() {
      try {
        const canvas = await this.generateHighResPNG();

        // Convert to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            this.showErrorSnackbar(null, this.$t("error_generating_image"));
            return;
          }

          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${this.sanitizedFileName}_600dpi.png`;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, "image/png");
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_downloading_file"));
      }
    },

    async downloadPDF() {
      try {
        // Dynamic import for jsPDF
        const { jsPDF } = await import("jspdf");

        // Create PDF document (A4 size, 300 DPI)
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        // Generate high-res canvas for PDF
        const canvas = await this.generateHighResPNG();
        const imgData = canvas.toDataURL("image/png");

        // Calculate dimensions for centered QR (5cm x 5cm)
        const qrSize = 50; // 5cm
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const x = (pageWidth - qrSize) / 2;
        const y = (pageHeight - qrSize) / 2;

        // Add title
        pdf.setFontSize(16);
        pdf.text(`QR Kod - ${this.title}`, pageWidth / 2, y - 20, { align: "center" });

        // Add QR code
        pdf.addImage(imgData, "PNG", x, y, qrSize, qrSize);

        // Add QR content below
        pdf.setFontSize(10);
        pdf.text(`Sadržaj: ${this.qrCode}`, pageWidth / 2, y + qrSize + 15, {
          align: "center",
        });

        // Add printing instructions
        pdf.setFontSize(8);
        const instructions = [
          "Preporučene dimenzije za štampu:",
          "• Minimum 2cm x 2cm za vizitke",
          "• 3cm x 3cm ili veće za postere",
          "• Ostaviti 4mm margine oko QR koda",
        ];

        instructions.forEach((line, index) => {
          pdf.text(line, 20, y + qrSize + 35 + index * 5);
        });

        // Save PDF
        pdf.save(`${this.sanitizedFileName}.pdf`);
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_downloading_file"));
      }
    },

    downloadEPS() {
      try {
        const svgContent = this.generateSVG();
        if (!svgContent) {
          this.showErrorSnackbar(null, this.$t("no_qr_code_to_download"));
          return;
        }

        // Convert SVG to EPS format
        const epsContent = this.svgToEPS(svgContent);

        // Create blob and download
        const blob = new Blob([epsContent], { type: "application/postscript" });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.sanitizedFileName}.eps`;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_downloading_file"));
      }
    },

    svgToEPS(svgContent) {
      // Basic SVG to EPS conversion
      const epsHeader = `%!PS-Adobe-3.0 EPSF-3.0
%%BoundingBox: 0 0 400 400
%%HiResBoundingBox: 0.0 0.0 400.0 400.0
%%Creator: QR Code Generator
%%Title: QR Code - ${this.title}
%%CreationDate: ${new Date().toISOString()}
%%DocumentData: Clean7Bit
%%Origin: 0 0
%%LanguageLevel: 2
%%Pages: 1
%%Page: 1 1
`;

      // Extract path data from SVG and convert to PostScript
      const pathRegex = /<path[^>]*d="([^"]*)"[^>]*>/g;
      let pathCommands = "";
      let match;

      while ((match = pathRegex.exec(svgContent)) !== null) {
        const pathData = match[1];
        // Convert SVG path to PostScript (simplified)
        pathCommands += this.svgPathToPS(pathData);
      }

      const epsFooter = `
showpage
%%EOF`;

      return epsHeader + pathCommands + epsFooter;
    },

    svgPathToPS(pathData) {
      // Simplified SVG path to PostScript conversion
      let psCommands = "newpath\n";

      const commands = pathData.match(/[MLZ][^MLZ]*/g) || [];

      commands.forEach((cmd) => {
        const type = cmd[0];
        const coords = cmd
          .slice(1)
          .trim()
          .split(/[\s,]+/)
          .filter((x) => x);

        switch (type) {
          case "M":
            if (coords.length >= 2) {
              psCommands += `${coords[0]} ${400 - coords[1]} moveto\n`;
            }
            break;
          case "L":
            if (coords.length >= 2) {
              psCommands += `${coords[0]} ${400 - coords[1]} lineto\n`;
            }
            break;
          case "Z":
            psCommands += "closepath\n";
            break;
        }
      });

      psCommands += "fill\n";
      return psCommands;
    },

    downloadSVG() {
      try {
        const svgContent = this.generateSVG();
        if (!svgContent) {
          this.showErrorSnackbar(null, this.$t("no_qr_code_to_download"));
          return;
        }

        // Create blob and download
        const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.sanitizedFileName}.svg`;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_downloading_file"));
      }
    },
  },
};
</script>
